/**
 * After each registry operation (ADR-024): what the dossier & compliance core ran — the
 * registrar signature ("chờ NĐK ký"), the dossier sync to VNNIC HSTM (.vn), the change report to
 * VNNIC's international-domain reporting (other TLDs) — and the registrar-signature queue and its
 * daily batches. Mirrors subsystems/identity/facade/adapters/admin-api/src/admin/post_operations.rs and
 * crates/compliance/src/{post_operation,registrar_signature}.rs.
 */

import type { CallOptions, ISODateTime, ItemList, WriteOptions } from '../types';

export const RUN_STATUSES = ['IN_PROGRESS', 'ATTENTION', 'COMPLETED', 'NOTHING_REQUIRED'] as const;
export type PostOperationRunStatus = (typeof RUN_STATUSES)[number];

export type PostAction = 'REGISTRAR_SIGNATURE' | 'DOSSIER_SYNC' | 'CHANGE_REPORT';
export type PostOperationStepStatus =
  | 'WAITING'
  | 'QUEUED'
  | 'REQUESTED'
  | 'ACCEPTED'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'REJECTED'
  | 'FAILED'
  | 'NOT_SUPPORTED'
  | 'DISABLED'
  /** The dossier was replaced after the operation: its replacement is checked and signed instead. */
  | 'SUPERSEDED';

export type DomainOperation =
  | 'REGISTER'
  | 'RENEW'
  | 'TRANSFER_IN'
  | 'TRANSFER_OUT'
  | 'CHANGE_REGISTRANT'
  | 'NAME_CHANGE'
  | 'UPDATE_CONTACTS'
  | 'RESTORE'
  | 'DELETE';

export interface PostOperationStep {
  id: string;
  position: number;
  action: PostAction;
  /** `vnnic-hstm`, `vnnic-gtld`; null for the registrar signature. */
  connector: string | null;
  status: PostOperationStepStatus;
  detail: string | null;
  reference: string | null;
  requested_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  updated_at: ISODateTime;
}

export interface PostOperationRun {
  id: string;
  source_event_id: string;
  domain_id: string;
  ascii_name: string;
  zone: string;
  tld_class: string;
  operation: DomainOperation;
  occurred_at: ISODateTime;
  requirement_group: string;
  requirements_version: string;
  dossier_id: string | null;
  status: PostOperationRunStatus;
  created_at: ISODateTime;
  completed_at: ISODateTime | null;
  steps: PostOperationStep[];
}

/**
 * PENDING_CHECK: waiting for the checks before signing (customer verification, the declaration
 * matching the subject and the domain's contacts, required files); REVIEW: a check failed —
 * staff fix the dossier (work item REGISTRAR_SIGNATURE_PRECHECK), never sent to the signing app.
 */
export const QUEUE_STATUSES = ['PENDING_CHECK', 'REVIEW', 'QUEUED', 'IN_BATCH', 'SIGNED', 'CANCELLED'] as const;
export type SignatureItemStatus = (typeof QUEUE_STATUSES)[number];

export interface SignatureQueueItem {
  id: string;
  dossier_id: string;
  domain_id: string;
  ascii_name: string;
  /** One item per file: AUTO (signed automatically: dossier.registrar_auto_sign_files) or MANUAL ("Gửi NĐK ký"). Absent from older servers. */
  origin?: 'AUTO' | 'MANUAL';
  /** DOMAIN_DECLARATION, another automatic kind, or DOSSIER_FILE (manual). */
  file_kind?: string;
  /** The file staff chose (manual requests). */
  document_version_id?: string | null;
  purpose?: string | null;
  requested_by?: string | null;
  status: SignatureItemStatus;
  attempts: number;
  /** Times a returned file did not match this dossier ("lỗi khớp file"). */
  mismatches: number;
  queued_at: ISODateTime;
  signed_at: ISODateTime | null;
  signed_sha256: string | null;
  last_error: string | null;
  /** The checks before signing: [{ code, status, message }]. */
  precheck?: { code: string; status: string; message?: string }[] | null;
  prechecked_at?: ISODateTime | null;
  precheck_work_item_id?: string | null;
}

export type SignatureBatchStatus =
  | 'EMPTY'
  | 'QUEUED'
  | 'SENT'
  | 'WAITING_APPROVAL'
  | 'SIGNED'
  | 'PARTIALLY_FAILED'
  | 'EXPIRED'
  | 'REJECTED'
  | 'FAILED';

export interface SignatureTransaction {
  id: string;
  chunk_no: number;
  status: 'PREPARING' | 'WAITING_APPROVAL' | 'SIGNED' | 'PARTIALLY_FAILED' | 'EXPIRED' | 'REJECTED' | 'FAILED';
  item_count: number;
  sent_at: ISODateTime | null;
  expires_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  last_error: string | null;
}

export interface SignatureBatchItem {
  id: string;
  dossier_id: string;
  domain_id: string;
  ascii_name: string;
  origin?: 'AUTO' | 'MANUAL';
  file_kind?: string;
  /** Binding purpose of the file sent. */
  purpose?: string | null;
  doc_id: string;
  position: number | null;
  status: 'PENDING' | 'SIGNED' | 'MISMATCH' | 'FAILED' | 'EXPIRED' | 'REJECTED';
  unsigned_sha256: string | null;
  signed_sha256: string | null;
  error: string | null;
  completed_at: ISODateTime | null;
}

export interface SignatureBatch {
  id: string;
  batch_date: string;
  trigger: 'SCHEDULED' | 'MANUAL';
  status: SignatureBatchStatus;
  signer: string;
  certificate_serial: string | null;
  item_count: number;
  signed_count: number;
  failed_count: number;
  requested_by: string;
  sent_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  last_error: string | null;
  created_at: ISODateTime;
  transactions: SignatureTransaction[];
  items: SignatureBatchItem[];
}

export interface SignatureSettings {
  /** Local HH:MM of the daily batch. */
  daily_at: string;
  utc_offset_minutes: number;
  /** Documents per signing transaction (one confirmation in the app), ≤ 100. */
  chunk_size: number;
  approval_window_seconds: number;
  signer: 'MOCK' | 'SMARTCA' | 'OFF';
}

export interface SignatureBatchList extends ItemList<SignatureBatch> {
  settings: SignatureSettings;
}

export interface RequirementGroup {
  key: string;
  label: { vi: string; en: string };
  tld_classes: string[];
  zones: string[];
  source: string;
  verification: Record<string, string[]>;
  operations: Record<DomainOperation, PostAction[]>;
  connectors: Partial<Record<PostAction, string>>;
}

export interface ComplianceRequirements {
  version: string;
  origin: string;
  groups: RequirementGroup[];
}

export interface PostOperationQuery {
  status?: PostOperationRunStatus;
  domain_id?: string;
  limit?: number;
}

/** VNNIC daily Domain Tracking Report (subsystems/identity/facade/adapters/admin-api/src/admin/tracking_reports.rs). */
export const TRACKING_STATUSES = ['PENDING', 'SENDING', 'SENT', 'RETRY_WAIT', 'FAILED'] as const;
export type TrackingReportStatus = (typeof TRACKING_STATUSES)[number];

/** The eight counts sent to VNNIC (the DTO's field names). */
export interface TrackingCounts {
  availableDomainRequest: number;
  availableDomainIssued: number;
  bizFreeRequest: number;
  bizFreeIssued: number;
  idFreeRequest: number;
  idFreeIssued: number;
  suggestedDomainRequest: number;
  suggestedDomainIssued: number;
}

export interface TrackingAttempt {
  attempt: number;
  trigger: 'SCHEDULED' | 'MANUAL';
  method: 'POST' | 'PUT' | null;
  outcome: 'SENT' | 'FAILED' | 'NOT_SENT';
  http_status: number | null;
  error: string | null;
  counts: TrackingCounts | null;
  actor: string;
  at: ISODateTime;
}

export interface TrackingReport {
  id: string;
  /** The reported day (YYYY-MM-DD, local Asia/Ho_Chi_Minh). */
  stats_date: string;
  status: TrackingReportStatus;
  trigger: 'SCHEDULED' | 'MANUAL';
  attempts: number;
  next_attempt_at: ISODateTime | null;
  counts: TrackingCounts | null;
  /** POST (new) or PUT (corrected) of the last attempt VNNIC accepted. */
  method: 'POST' | 'PUT' | null;
  http_status: number | null;
  last_error: string | null;
  sent_at: ISODateTime | null;
  requested_by: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  history: TrackingAttempt[];
}

export interface TrackingReportList extends ItemList<TrackingReport> {
  /** Yesterday (local): the day the next report is about. */
  default_stats_date: string;
  /** Whether admin-api has the VNNIC Registrar API configured. */
  configured: boolean;
}

/** One `.vn` dossier "Chạy bù" starts a run for (apps/api/admin post_operations::backfill). */
export interface BackfillItem {
  domain_id: string;
  ascii_name: string;
  dossier_id: string;
  dossier_completed_at: ISODateTime | null;
  operation: DomainOperation;
  /** EVENT: the domain's latest completed operation; DOSSIER: no event (imported), keyed by the dossier. */
  source: 'EVENT' | 'DOSSIER';
  source_event_id: string | null;
  /** The run created (null in a dry run, or when its source already had one). */
  run_id: string | null;
}

export interface BackfillReport {
  dry_run: boolean;
  since: string;
  candidates: number;
  from_events: number;
  from_dossiers: number;
  created: number;
  already: number;
  by_operation: Partial<Record<DomainOperation, number>>;
  /** The first 200 candidates. */
  items: BackfillItem[];
  /** More than one pass handles: run it again. */
  more: boolean;
}

export interface PostOperationsApi {
  listPostOperations(query: PostOperationQuery, opts?: CallOptions): Promise<ItemList<PostOperationRun>>;
  getComplianceRequirements(opts?: CallOptions): Promise<ComplianceRequirements>;
  /** "Chạy bù": the missing runs of .vn dossiers completed since `since` (dry_run: count only). */
  backfillPostOperations(body: { since: string; dry_run: boolean }, opts?: WriteOptions): Promise<BackfillReport>;
  listSignatureQueue(query: { status?: SignatureItemStatus; limit?: number }, opts?: CallOptions): Promise<ItemList<SignatureQueueItem>>;
  listSignatureBatches(opts?: CallOptions): Promise<SignatureBatchList>;
  /** "Gửi ký ngay": `{ item_id }` batches only that item now; `{ all: true }` the whole queue. */
  signQueuedNow(scope: { item_id: string } | { all: true }, opts?: WriteOptions): Promise<{ batch: { batch_id: string; status: string; item_count: number } }>;
  updateSignatureSettings(body: SignatureSettings, opts?: WriteOptions): Promise<SignatureSettings>;
  /** The latest days of the VNNIC daily Domain Tracking Report. */
  listTrackingReports(query: { limit?: number }, opts?: CallOptions): Promise<TrackingReportList>;
  /** The counts of a past day as they would be sent now (nothing is sent). */
  previewTrackingReport(date: string, opts?: CallOptions): Promise<{ stats_date: string; counts: TrackingCounts }>;
  /** "Gửi lại": counts the day again and sends it to VNNIC now (PUT when VNNIC holds it). */
  sendTrackingReport(date: string, opts?: WriteOptions): Promise<{ report: TrackingReport }>;
}
